import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Car, X, FileText, Wrench, IndianRupee, TrendingUp, UserSquare } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import VehicleServicesDialog from "./VehicleServicesDialog";
import VehicleAssignmentDialog from "./VehicleAssignmentDialog";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const CATEGORIES = ["Bus", "Car", "Van", "Truck", "Two-wheeler", "Ambulance", "Tractor", "Other"];
const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid", "LPG"];
const STATUSES = ["Active", "Under Repair", "Breakdown", "Sold", "Scrapped"];
const DOC_TYPES = ["RC", "Insurance", "PUC", "Fitness", "Permit", "Road Tax", "Other"];

const empty = {
  id: "",
  registration_number: "",
  category: "Bus",
  make: "",
  model_name: "",
  variant: "",
  fuel_type: "Diesel",
  manufacturing_year: "",
  chassis_number: "",
  engine_number: "",
  purchase_date: "",
  purchase_price: 0,
  supplier: "",
  invoice_number: "",
  purchaser_name: "",
  institute_id: "",
  branch: "",
  department: "",
  location: "",
  seating_capacity: 0,
  load_capacity_kg: 0,
  odometer_km: 0,
  status: "Active",
  photo_url: "",
  photo_public_id: "",
  documents: [],
  remarks: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-3" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

const statusColor = {
  "Active": "bg-green-100 text-green-800 border-green-300",
  "Under Repair": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Breakdown": "bg-red-100 text-red-800 border-red-300",
  "Sold": "bg-gray-100 text-gray-700 border-gray-300",
  "Scrapped": "bg-gray-200 text-gray-800 border-gray-400",
};

export default function Vehicles() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor", "vehicle_admin"].includes(role);
  const [items, setItems] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [servicesFor, setServicesFor] = useState(null); // vehicle whose services drawer is open
  const [assignFor, setAssignFor] = useState(null); // vehicle whose assignment drawer is open
  const [tcoMap, setTcoMap] = useState({}); // vehicle_id -> {service_cost, total_cost_of_ownership}
  const [defaultPurchaser, setDefaultPurchaser] = useState(""); // trust name — default for purchaser_name on Add

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [{ data }, owners, summary] = await Promise.all([
        api.get("/admin/vehicles"),
        api.get("/admin/vehicles/assignable-owners").catch(() => ({ data: [] })),
        api.get("/admin/vehicles-summary").catch(() => ({ data: null })),
      ]);
      setItems(data);
      setInstitutes(owners.data || []);
      // Trust is always the first entry of assignable-owners (type: "trust")
      const trust = (owners.data || []).find((o) => o.type === "trust");
      if (trust) setDefaultPurchaser(trust.name);
      const map = {};
      const spend = summary.data?.costs?.spend_by_vehicle || {};
      for (const v of data) {
        const s = Number(spend[v.id] || 0);
        map[v.id] = { service_cost: s, total_cost_of_ownership: Number(v.purchase_price || 0) + s };
      }
      setTcoMap(map);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load vehicles");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((v) => {
      if (statusFilter && v.status !== statusFilter) return false;
      if (!term) return true;
      return [v.registration_number, v.make, v.model_name, v.chassis_number, v.institute_name]
        .some((x) => (x || "").toLowerCase().includes(term));
    });
  }, [items, q, statusFilter]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm({ ...empty, purchaser_name: defaultPurchaser || "" }); setOpen(true); };
  const openEdit = (v) => {
    setForm({
      ...empty,
      ...v,
      documents: Array.isArray(v.documents) ? v.documents : [],
    });
    setOpen(true);
  };

  const save = async () => {
    const reg = (form.registration_number || "").trim();
    if (!reg) return toast.error("Registration number is required");
    const payload = {
      ...form,
      registration_number: reg,
      seating_capacity: Number(form.seating_capacity) || 0,
      load_capacity_kg: Number(form.load_capacity_kg) || 0,
      odometer_km: Number(form.odometer_km) || 0,
      purchase_price: Number(form.purchase_price) || 0,
      documents: (form.documents || []).map((d) => ({
        doc_type: d.doc_type || "Other",
        issue_date: d.issue_date || "",
        expiry_date: d.expiry_date || "",
        file_url: d.file_url || "",
        file_public_id: d.file_public_id || "",
        remarks: d.remarks || "",
      })),
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/vehicles/${form.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/admin/vehicles", payload);
        toast.success("Vehicle added");
      }
      setOpen(false); fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/vehicles/${confirmDelete.id}`);
      toast.success("Vehicle deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const addDoc = () => setForm((f) => ({ ...f, documents: [...(f.documents || []), { doc_type: "RC", issue_date: "", expiry_date: "", file_url: "", file_public_id: "", remarks: "" }] }));
  const setDoc = (i, patch) => setForm((f) => ({ ...f, documents: (f.documents || []).map((d, j) => (j === i ? { ...d, ...patch } : d)) }));
  const removeDoc = (i) => setForm((f) => ({ ...f, documents: (f.documents || []).filter((_, j) => j !== i) }));

  return (
    <div className="space-y-6" data-testid="admin-vehicles">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Vehicle Maintenance</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Vehicle Master</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Registration, ownership, capacity, compliance documents.</div>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} data-testid="veh-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Add Vehicle
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-white border border-[#e5e0d8] rounded-sm p-3">
        <input data-testid="veh-search" className={`${inputCls} flex-1 min-w-[220px]`} placeholder="Search reg no · make · model · chassis · institute" value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="veh-status-filter" className={`${inputCls} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <Car size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No vehicles match your filters.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f5f1e8] text-[10px] tracking-widest uppercase text-[#6b655b]">
              <tr>
                <th className="text-left px-4 py-3">Reg. No</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Fuel</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-right px-4 py-3">Odometer (km)</th>
                <th className="text-right px-4 py-3">TCO (₹)</th>
                <th className="text-left px-4 py-3">Docs</th>
                <th className="text-left px-4 py-3">Status</th>
                {canEdit && <th className="text-right px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0ece2]">
              {filtered.map((v) => (
                <tr key={v.id} data-testid={`veh-row-${v.id}`} className="hover:bg-[#faf8f2]">
                  <td className="px-4 py-3 font-semibold text-[#1c1a17]">{v.registration_number}{v.category ? ` (${v.category})` : ""}</td>
                  <td className="px-4 py-3">
                    <div className="text-[#1c1a17]">{v.make} {v.model_name}</div>
                    <div className="text-[11px] text-[#8a8478]">{[v.category, v.variant, v.manufacturing_year].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td className="px-4 py-3">{v.fuel_type || "—"}</td>
                  <td className="px-4 py-3">{v.institute_name || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{Number(v.odometer_km || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`veh-tco-${v.id}`}>
                    <div className="font-semibold text-[#1c1a17]">₹ {Math.round(tcoMap[v.id]?.total_cost_of_ownership || Number(v.purchase_price || 0)).toLocaleString("en-IN")}</div>
                    {(tcoMap[v.id]?.service_cost || 0) > 0 && (
                      <div className="text-[10px] text-[#8a8478]">+ ₹ {Math.round(tcoMap[v.id].service_cost).toLocaleString("en-IN")} serviced</div>
                    )}
                  </td>
                  <td className="px-4 py-3">{(v.documents || []).length}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-sm border ${statusColor[v.status] || "bg-gray-100 text-gray-700 border-gray-300"}`}>{v.status}</span>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button type="button" onClick={() => setAssignFor(v)} data-testid={`veh-assign-${v.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><UserSquare size={11} />Assign</button>
                      <button type="button" onClick={() => setServicesFor(v)} data-testid={`veh-services-${v.id}`} className="inline-flex items-center gap-1 text-[#0D5C63] hover:bg-[#0D5C63]/10 text-[11px] font-semibold px-2 py-1 rounded-sm ml-1"><Wrench size={11} />Services</button>
                      <button type="button" onClick={() => openEdit(v)} data-testid={`veh-edit-${v.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm ml-1"><Pencil size={11} />Edit</button>
                      <button type="button" onClick={() => setConfirmDelete(v)} data-testid={`veh-delete-${v.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm ml-1"><Trash2 size={11} />Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
            <DialogDescription>Master record for a single vehicle — identity, purchase, assignment, capacity and compliance documents.</DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Registration Number *">
              <input data-testid="veh-input-reg" className={inputCls} value={form.registration_number} onChange={(e) => set("registration_number", e.target.value.toUpperCase())} />
            </Field>
            <Field label="Category">
              <select data-testid="veh-input-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="veh-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                {STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>

            <Field label="Make">
              <input data-testid="veh-input-make" className={inputCls} value={form.make} onChange={(e) => set("make", e.target.value)} />
            </Field>
            <Field label="Model">
              <input data-testid="veh-input-model" className={inputCls} value={form.model_name} onChange={(e) => set("model_name", e.target.value)} />
            </Field>
            <Field label="Variant">
              <input data-testid="veh-input-variant" className={inputCls} value={form.variant} onChange={(e) => set("variant", e.target.value)} />
            </Field>

            <Field label="Fuel Type">
              <select data-testid="veh-input-fuel" className={inputCls} value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)}>
                <option value="">—</option>
                {FUEL_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Manufacturing Year">
              <input data-testid="veh-input-year" type="number" min="1900" max="2100" className={inputCls} value={form.manufacturing_year} onChange={(e) => set("manufacturing_year", e.target.value)} />
            </Field>
            <Field label="Odometer (km)">
              <input data-testid="veh-input-odo" type="number" min="0" step="0.1" className={inputCls} value={form.odometer_km} onChange={(e) => set("odometer_km", e.target.value)} />
            </Field>

            <Field label="Chassis Number">
              <input data-testid="veh-input-chassis" className={inputCls} value={form.chassis_number} onChange={(e) => set("chassis_number", e.target.value)} />
            </Field>
            <Field label="Engine Number">
              <input data-testid="veh-input-engine" className={inputCls} value={form.engine_number} onChange={(e) => set("engine_number", e.target.value)} />
            </Field>
            <Field label="Purchase Date">
              <input data-testid="veh-input-pdate" type="date" className={inputCls} value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} />
            </Field>

            <Field label="Purchase Price (₹)">
              <input data-testid="veh-input-price" type="number" min="0" step="0.01" className={inputCls} value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} />
            </Field>
            <Field label="Supplier">
              <input data-testid="veh-input-supplier" className={inputCls} value={form.supplier} onChange={(e) => set("supplier", e.target.value)} />
            </Field>
            <Field label="Invoice Number">
              <input data-testid="veh-input-invoice" className={inputCls} value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} />
            </Field>

            <Field label="Purchaser Name">
              <input data-testid="veh-input-purchaser" className={inputCls} maxLength={200} value={form.purchaser_name} onChange={(e) => set("purchaser_name", e.target.value)} placeholder="Defaults to trust name; edit if a different owner purchased the vehicle" />
            </Field>

            <Field label="Seating Capacity">
              <input data-testid="veh-input-seat" type="number" min="0" className={inputCls} value={form.seating_capacity} onChange={(e) => set("seating_capacity", e.target.value)} />
            </Field>
            <Field label="Load Capacity (kg)">
              <input data-testid="veh-input-load" type="number" min="0" step="0.1" className={inputCls} value={form.load_capacity_kg} onChange={(e) => set("load_capacity_kg", e.target.value)} />
            </Field>

            <Field label="Vehicle Photograph" full>
              <FileUploader
                kind="vehicle_photo"
                label=""
                url={form.photo_url}
                onChange={({ url, public_id }) => setForm((f) => ({ ...f, photo_url: url || "", photo_public_id: public_id || "" }))}
                testid="veh-input-photo"
              />
            </Field>

            <Field label="Remarks" full>
              <textarea data-testid="veh-input-remarks" rows={3} maxLength={1000} className={`${inputCls} resize-y`} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
            </Field>
          </div>

          <div className="mt-6">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Compliance Documents</div>
              <button type="button" onClick={addDoc} data-testid="veh-doc-add" className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm border border-[#234333]/30"><Plus size={12} />Add Document</button>
            </div>
            {(form.documents || []).length === 0 ? (
              <div className="text-xs text-[#8a8478] bg-white border border-dashed border-[#e5e0d8] rounded-sm p-4 text-center">No documents added yet.</div>
            ) : (
              <div className="space-y-3">
                {(form.documents || []).map((d, i) => (
                  <div key={i} data-testid={`veh-doc-${i}`} className="bg-white border border-[#e5e0d8] rounded-sm p-3 grid md:grid-cols-4 gap-3">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1">Type</div>
                      <select data-testid={`veh-doc-type-${i}`} className={inputCls} value={d.doc_type} onChange={(e) => setDoc(i, { doc_type: e.target.value })}>
                        {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1">Issue Date</div>
                      <input data-testid={`veh-doc-issue-${i}`} type="date" className={inputCls} value={d.issue_date} onChange={(e) => setDoc(i, { issue_date: e.target.value })} />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1">Expiry Date</div>
                      <input data-testid={`veh-doc-expiry-${i}`} type="date" className={inputCls} value={d.expiry_date} onChange={(e) => setDoc(i, { expiry_date: e.target.value })} />
                    </div>
                    <div className="md:col-span-1">
                      <div className="flex items-start justify-between">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1">File</div>
                        <button type="button" onClick={() => removeDoc(i)} aria-label="Remove" data-testid={`veh-doc-remove-${i}`} className="text-red-700 hover:text-red-900 p-1"><X size={14} /></button>
                      </div>
                      <FileUploader
                        kind="vehicle_document"
                        label=""
                        url={d.file_url}
                        onChange={({ url, public_id }) => setDoc(i, { file_url: url || "", file_public_id: public_id || "" })}
                        testid={`veh-doc-file-${i}`}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1">Remarks</div>
                      <input data-testid={`veh-doc-remarks-${i}`} className={inputCls} value={d.remarks || ""} onChange={(e) => setDoc(i, { remarks: e.target.value })} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="veh-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update Vehicle" : "Add Vehicle")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete vehicle?</AlertDialogTitle>
            <AlertDialogDescription>
              Vehicle <b>{confirmDelete?.registration_number}</b> and all uploaded documents will be permanently removed. Any driver assigned to this vehicle will be unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="veh-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <VehicleServicesDialog
        vehicle={servicesFor}
        canEdit={canEdit}
        onClose={() => setServicesFor(null)}
        onDirty={fetchAll}
      />
      <VehicleAssignmentDialog
        vehicle={assignFor}
        canEdit={canEdit}
        onClose={() => setAssignFor(null)}
        onSaved={fetchAll}
      />
    </div>
  );
}
